import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Search, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

function today() {
  return new Date().toISOString().slice(0, 10);
}

type Check = {
  id: number;
  number?: string;
  issuingBank?: string;
  drawer?: string;
  partyLabel?: string;
  purpose?: string;
  amount: number;
  receivedDate?: string;
  depositDate?: string;
  status: string;
};

type DepositSummary = {
  id: number;
  depositDate?: string;
  checkCount: number;
  total: number;
};

export default function Checks() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'checks' | 'deposits'>('checks');

  return (
    <div>
      <header className="mb-6">
        <p className="eyebrow">{t('nav.checks')}</p>
        <h1 className="page-title mt-1">{t('checks.title')}</h1>
      </header>

      <div className="mb-5 flex gap-1 rounded-lg bg-surface-raised p-1 sm:w-fit">
        <button
          type="button"
          onClick={() => setTab('checks')}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors sm:flex-none ${
            tab === 'checks' ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
          }`}
        >
          {t('checks.tabChecks')}
        </button>
        <button
          type="button"
          onClick={() => setTab('deposits')}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors sm:flex-none ${
            tab === 'deposits' ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
          }`}
        >
          {t('checks.tabDeposits')}
        </button>
      </div>

      {tab === 'checks' ? <ChecksTab /> : <DepositsTab />}
    </div>
  );
}

function ChecksTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery<Check[]>({
    queryKey: ['checks'],
    queryFn: () => api.get('/checks').then((r) => r.data),
  });

  const checks = data ?? [];

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [depositDate, setDepositDate] = useState(today());
  const [bankAccount, setBankAccount] = useState('512');
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Check | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/checks/${id}`).then((r) => r.data),
    onSuccess: () => {
      setDetail(null);
      qc.invalidateQueries({ queryKey: ['checks'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('checks.deleteError')),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return checks;
    return checks.filter((c) =>
      [c.number, c.drawer, c.partyLabel, c.purpose, String(c.amount)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [checks, search]);

  const receivedIds = useMemo(
    () => filtered.filter((c) => c.status === 'RECEIVED').map((c) => c.id),
    [filtered],
  );

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) =>
      prev.size === receivedIds.length ? new Set() : new Set<number>(receivedIds),
    );

  const deposit = useMutation({
    mutationFn: () =>
      api
        .post('/checks/deposit', {
          checkIds: Array.from(selected),
          depositDate,
          bankAccount: bankAccount || null,
        })
        .then((r) => r.data),
    onSuccess: () => {
      setSelected(new Set());
      setError(null);
      qc.invalidateQueries({ queryKey: ['checks'] });
      qc.invalidateQueries({ queryKey: ['check-deposits'] });
    },
    onError: (e: any) => {
      setError(e?.response?.data?.message ?? t('checks.depositError'));
    },
  });

  const selectedTotal = useMemo(
    () =>
      checks
        .filter((c) => selected.has(c.id))
        .reduce((s, c) => s + Number(c.amount || 0), 0),
    [checks, selected],
  );

  return (
    <div>
      <div className="mb-4 relative">
        <Search
          size={16}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('checks.search')}
          className="input w-full pl-9 sm:max-w-md"
        />
      </div>

      {selected.size > 0 && (
        <div className="card mb-4 flex flex-wrap items-end gap-4 p-4">
          <div>
            <label className="field-label">{t('checks.depositDate')}</label>
            <input
              type="date"
              value={depositDate}
              onChange={(e) => setDepositDate(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="field-label">{t('checks.bankAccount')}</label>
            <input
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              className="input tnum w-32"
            />
          </div>
          <div className="text-sm text-ink-muted">
            {t('checks.selected', { count: selected.size })} · {euro(selectedTotal)}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button
              onClick={() => deposit.mutate()}
              disabled={deposit.isPending}
              className="btn btn-primary"
            >
              {t('checks.depositSelected', { count: selected.size })}
            </button>
          </div>
        </div>
      )}

      <div className="card p-3">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={receivedIds.length > 0 && selected.size === receivedIds.length}
                    onChange={toggleAll}
                    disabled={receivedIds.length === 0}
                  />
                </th>
                <th>{t('field.received')}</th>
                <th>{t('field.number')}</th>
                <th>{t('field.drawer')}</th>
                <th className="num">{t('field.amount')}</th>
                <th>{t('field.status')}</th>
                <th className="text-right">{t('field.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const canSelect = c.status === 'RECEIVED';
                return (
                  <tr
                    key={c.id}
                    onClick={() => setDetail(c)}
                    className="cursor-pointer hover:bg-surface-raised"
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      {canSelect && (
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggle(c.id)}
                        />
                      )}
                    </td>
                    <td data-label={t('field.received')} className="whitespace-nowrap text-ink-soft">{dateFmt(c.receivedDate)}</td>
                    <td data-label={t('field.number')} className="font-medium tnum">{c.number ?? '-'}</td>
                    <td data-label={t('field.drawer')}>{c.drawer ?? c.partyLabel ?? c.purpose}</td>
                    <td data-label={t('field.amount')} className="num">{euro(c.amount)}</td>
                    <td data-label={t('field.status')} className="text-ink-soft">
                      {t(`checks.status.${c.status}`, { defaultValue: c.status })}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} className="cell-actions text-right">
                      <button
                        onClick={() => {
                          if (window.confirm(t('checks.confirmDelete'))) remove.mutate(c.id);
                        }}
                        className="btn btn-ghost !px-2 !py-1 text-expense"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {checks.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-ink-muted">
                    {t('checks.empty')}
                  </td>
                </tr>
              )}
              {checks.length > 0 && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-ink-muted">
                    {t('checks.noResults')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <CheckDetail
          check={detail}
          onClose={() => setDetail(null)}
          onDelete={() => {
            if (window.confirm(t('checks.confirmDelete'))) remove.mutate(detail.id);
          }}
        />
      )}
    </div>
  );
}

function DepositsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery<DepositSummary[]>({
    queryKey: ['check-deposits'],
    queryFn: () => api.get('/checks/deposits').then((r) => r.data),
  });

  const deposits = data ?? [];
  const [expanded, setExpanded] = useState<number | null>(null);

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/checks/deposits/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['check-deposits'] });
      qc.invalidateQueries({ queryKey: ['checks'] });
    },
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('checks.depositDeleteError')),
  });

  if (deposits.length === 0) {
    return <div className="card p-8 text-center text-ink-muted">{t('checks.depositsEmpty')}</div>;
  }

  return (
    <div className="space-y-3">
      {deposits.map((d) => (
        <DepositRow
          key={d.id}
          deposit={d}
          open={expanded === d.id}
          onToggle={() => setExpanded((cur) => (cur === d.id ? null : d.id))}
          onDelete={() => {
            if (window.confirm(t('checks.confirmDeleteDeposit'))) remove.mutate(d.id);
          }}
        />
      ))}
    </div>
  );
}

function DepositRow({
  deposit,
  open,
  onToggle,
  onDelete,
}: {
  deposit: DepositSummary;
  open: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<Check[]>({
    queryKey: ['check-deposit', deposit.id],
    queryFn: () => api.get(`/checks/deposits/${deposit.id}`).then((r) => r.data),
    enabled: open,
  });

  return (
    <div className="card overflow-hidden p-0">
      <div className="flex items-center gap-1 pr-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-3 p-4 text-left transition-colors hover:bg-surface-raised"
        >
          {open ? (
            <ChevronDown size={18} className="text-ink-muted" />
          ) : (
            <ChevronRight size={18} className="text-ink-muted" />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-medium text-ink">
              {t('checks.depositLabel', { date: dateFmt(deposit.depositDate) })}
            </div>
            <div className="text-sm text-ink-muted">
              {t('checks.depositCount', { count: deposit.checkCount })}
            </div>
          </div>
          <div className="text-right">
            <div className="num font-semibold text-ink">{euro(deposit.total)}</div>
            <div className="text-xs text-ink-muted">{t('checks.depositTotal')}</div>
          </div>
        </button>
        <button
          type="button"
          onClick={onDelete}
          title={t('common.delete')}
          className="btn btn-ghost !px-2 !py-1 text-expense"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {open && (
        <div className="border-t border-line px-4 py-3">
          {isLoading ? (
            <div className="py-4 text-center text-sm text-ink-muted">{t('common.loading')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('field.received')}</th>
                    <th>{t('field.number')}</th>
                    <th>{t('field.drawer')}</th>
                    <th className="num">{t('field.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data ?? []).map((c) => (
                    <tr key={c.id}>
                      <td data-label={t('field.received')} className="whitespace-nowrap text-ink-soft">{dateFmt(c.receivedDate)}</td>
                      <td data-label={t('field.number')} className="font-medium tnum">{c.number ?? '-'}</td>
                      <td data-label={t('field.drawer')}>{c.drawer ?? c.partyLabel ?? c.purpose}</td>
                      <td data-label={t('field.amount')} className="num">{euro(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CheckDetail({
  check,
  onClose,
  onDelete,
}: {
  check: Check;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const rows: Array<[string, string]> = [
    [t('field.number'), check.number ?? '-'],
    [t('checks.issuingBank'), check.issuingBank ?? '-'],
    [t('field.drawer'), check.drawer ?? check.partyLabel ?? '-'],
    [t('checks.purpose'), check.purpose ?? '-'],
    [t('field.amount'), euro(check.amount)],
    [t('field.received'), dateFmt(check.receivedDate)],
    [t('checks.depositDate'), check.depositDate ? dateFmt(check.depositDate) : '-'],
    [t('field.status'), t(`checks.status.${check.status}`, { defaultValue: check.status })],
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="page-title text-lg">{t('checks.detailTitle')}</h2>
        <dl className="mt-4 divide-y divide-line">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between py-2 text-sm">
              <dt className="text-ink-muted">{k}</dt>
              <dd className="font-medium text-ink">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onDelete} className="btn btn-ghost text-expense">
            {t('common.delete')}
          </button>
          <button onClick={onClose} className="btn btn-primary">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
